import { useCallback, useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Collapsible,
  CollapsiblePanel,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorTrigger,
  Field,
  FieldLabel,
  Icon,
  Input,
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
  RadioList,
  RadioListItem,
  Separator,
  Text,
  type EntitySelection,
  type EntitySelectorConfig,
} from "@salestrak/ui";

import { EntityGroupTag } from "#app/components/entity";
import { useIssueTypePool } from "#app/components/entity-selectors/hooks";
import { IssueTypePool } from "#app/components/entity-selectors/issue-type-pool";
import { WorkflowSelector } from "#app/components/entity-selectors/workflow-selector";
import {
  PaneScrollGutter,
  PaneScroller,
  SettingsSection,
  SwitchRow,
} from "#app/components/forms/form-builder/settings-rows";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { entityFromMemberOrTeam, type EntityData } from "#app/lib/entity-visuals";
import {
  DEFAULT_SUBMIT_LABEL,
  isOfferedAnywhere,
  MAX_SUBMISSION_LIMIT,
  type Form,
  type FormOffer,
  type FormSettings,
  type FormShare,
} from "#app/lib/forms";

/** `key` groups a run of edits to one setting into one undo step. */
export type FormSettingsChange = (
  key: keyof FormSettings,
  patch: (settings: FormSettings) => FormSettings,
) => void;

export const NO_OFFER: FormOffer = { issueTypeIds: [], workflowIds: [] };

/** Absent means default; a stored `undefined` would read as a change. */
export function without(settings: FormSettings, key: keyof FormSettings): FormSettings {
  const { [key]: _removed, ...rest } = settings;
  return rest;
}

export function FormSettingsPane({
  form,
  onChange,
  onRequiredByDefaultChange,
  onShare,
}: {
  form: Pick<Form, "formId" | "status" | "settings" | "requiredByDefault" | "sharedWith">;
  onChange: FormSettingsChange;
  onRequiredByDefaultChange: (requiredByDefault: boolean) => void;
  onShare: () => void;
}) {
  const settings = form.settings ?? {};

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      {/* The item pane's header rung, so switching tabs never moves the
          pane's first line. No close: the tabs above are how the pane goes. */}
      <Box align="center" direction="row" gap="sm" paddingX="md" paddingY="md">
        <Icon color="muted" name="settings" size="md" />
        <Text size="sm" weight="medium">
          Form settings
        </Text>
      </Box>
      <PaneScroller>
<Box direction="column">
        <PrivacySection
          onChange={onChange}
          onShare={onShare}
          settings={settings}
          sharedWith={form.sharedWith ?? []}
        />
        <Separator />
        <OfferSection onChange={onChange} settings={settings} />
        <Separator />
        <SettingsSection title="Submissions">
          <Field fill>
            <FieldLabel>Submission limit</FieldLabel>
            <SubmissionLimitField onChange={onChange} settings={settings} />
          </Field>
          <Field fill>
            <FieldLabel>Submit button</FieldLabel>
            <Input
              maxLength={40}
              onChange={(event) => {
                const submitLabel = event.target.value;
                onChange("submitLabel", (current) =>
                  submitLabel ? { ...current, submitLabel } : without(current, "submitLabel"),
                );
              }}
              placeholder={DEFAULT_SUBMIT_LABEL}
              value={settings.submitLabel ?? ""}
            />
          </Field>
          <SwitchRow
            checked={settings.resubmit === true && settings.submissionLimit !== 1}
            description={
              settings.submissionLimit === 1 ? "Off when limited to one" : "Offered after submitting"
            }
            disabled={settings.submissionLimit === 1}
            label="Submit another"
            onCheckedChange={(next) =>
              onChange("resubmit", (current) =>
                next ? { ...current, resubmit: true } : without(current, "resubmit"),
              )
            }
          />
        </SettingsSection>
        <Separator />
        <SettingsSection title="Defaults">
          <SwitchRow
            checked={form.requiredByDefault === true}
            description="Questions you add from now on start Required."
            label="New questions are required"
            onCheckedChange={onRequiredByDefaultChange}
          />
        </SettingsSection>
        <PaneScrollGutter />
      </Box>
</PaneScroller>
    </Box>
  );
}

function PrivacySection({
  settings,
  sharedWith,
  onChange,
  onShare,
}: {
  settings: FormSettings;
  sharedWith: readonly FormShare[];
  onChange: FormSettingsChange;
  onShare: () => void;
}) {
  const staged = settings.visibility ?? "private";
  const maps = useWorkspaceData();
  const shared = useMemo<EntityData[]>(
    () =>
      sharedWith.flatMap((share) => {
        const entity = entityFromMemberOrTeam(maps, share.id);
        return entity ? [entity] : [];
      }),
    [maps, sharedWith],
  );

  return (
    <SettingsSection title="Who can see submissions">
      <RadioList
        aria-label="Who can see submissions"
        onValueChange={(value) =>
          onChange("visibility", (current) =>
            value === "public"
              ? { ...current, visibility: "public" }
              : without(current, "visibility"),
          )
        }
        value={staged}
      >
        <RadioListItem icon={<Icon name="lock" size="sm" />} label="Private" value="private" />
        <RadioListItem icon={<Icon name="globe" size="sm" />} label="Public" value="public" />
      </RadioList>
      <Text size="xs" variant="muted">
        {staged === "public"
          ? "Anyone can see its submissions."
          : "Only whoever filled it out, people it's shared with, and admins."}
      </Text>
      {/* Public reaches everyone, so the share row folds away in place. */}
      <Collapsible open={staged === "private"}>
        <CollapsiblePanel stackGap="md">
          <Box align="center" direction="row" gap="md" justify="between">
            <Box align="center" direction="row" gap="sm" minWidth={0}>
              <Text size="sm">Shared with</Text>
              {shared.length > 0 ? (
                <EntityGroupTag entities={shared} max={4} size="xs" />
              ) : (
                <Badge tone="outline">Nobody</Badge>
              )}
            </Box>
            <Button onClick={onShare} size="sm" variant="outline">
              <Icon name="share" size="sm" />
              <Text size="sm">Share</Text>
            </Button>
          </Box>
        </CollapsiblePanel>
      </Collapsible>
    </SettingsSection>
  );
}

function OfferSection({
  settings,
  onChange,
}: {
  settings: FormSettings;
  onChange: FormSettingsChange;
}) {
  const offer = settings.offeredOn;
  const setOffer = (next: FormOffer) =>
    onChange("offeredOn", (current) => ({ ...current, offeredOn: next }));

  return (
    <SettingsSection title="Offered on">
      <RadioList
        aria-label="Offered on"
        onValueChange={(value) =>
          onChange("offeredOn", (current) =>
            value === "some"
              ? { ...current, offeredOn: current.offeredOn ?? NO_OFFER }
              : without(current, "offeredOn"),
          )
        }
        value={offer ? "some" : "all"}
      >
        <RadioListItem label="All Projects" value="all" />
        <RadioListItem label="Some Projects" value="some" />
      </RadioList>
      {offer ? (
        <>
          <Field fill>
            <FieldLabel>Workflows</FieldLabel>
            <WorkflowSelector
              chips
              fill
              maxSelections={0}
              onValueChange={(workflowIds) => setOffer({ ...offer, workflowIds })}
              placeholder="Add workflows..."
              value={offer.workflowIds}
            />
          </Field>
          <Field fill>
            <FieldLabel>Project types</FieldLabel>
            <IssueTypesPicker
              onValueChange={(issueTypeIds) => setOffer({ ...offer, issueTypeIds })}
              value={offer.issueTypeIds}
            />
          </Field>
          {isOfferedAnywhere({ settings }) ? (
            <Text size="xs" variant="muted">
              Offered on a Project in any of these workflows, or of any of these types.
            </Text>
          ) : (
            <Text size="xs" variant="warning">
              Not offered on any Project yet.
            </Text>
          )}
        </>
      ) : null}
    </SettingsSection>
  );
}

export function SubmissionLimitField({
  settings,
  onChange,
}: {
  settings: FormSettings;
  onChange: FormSettingsChange;
}) {
  return (
    <NumberField
      max={MAX_SUBMISSION_LIMIT}
      min={0}
      onValueChange={(next, details) => {
        // An empty field steps to 0 first; + means start limiting.
        const limit = next === null || next < 1 ? (details.reason === "increment-press" ? 1 : null) : next;
        onChange("submissionLimit", (current) =>
          limit === null ? without(current, "submissionLimit") : { ...current, submissionLimit: limit },
        );
      }}
      size="sm"
      value={settings.submissionLimit ?? null}
    >
      <NumberFieldGroup>
        <NumberFieldDecrement aria-label="Fewer submissions" />
        <NumberFieldInput aria-label="Submission limit" placeholder="Unlimited" />
        <NumberFieldIncrement aria-label="More submissions" />
      </NumberFieldGroup>
    </NumberField>
  );
}

export function IssueTypesPicker({
  value,
  onValueChange,
}: {
  value: readonly string[];
  onValueChange: (issueTypeIds: string[]) => void;
}) {
  const { issueTypes, issueTypesKnown } = useWorkspaceData();
  const { pool: typePool } = useIssueTypePool({ issueTypes });
  // An unfetched list is not an empty one.
  const pool = useMemo(
    () => (issueTypesKnown ? typePool : { ...typePool, loading: true }),
    [issueTypesKnown, typePool],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      emptyMessage: "No matching Project types",
      maxSelections: 0,
      noItemsMessage: "No Project types yet",
      pools: [pool],
      searchPlaceholder: "Find type...",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return value.flatMap((id) => {
      const entity = itemMap.get(id);
      return entity ? [{ entity, id, poolId: pool.id }] : [];
    });
  }, [pool, value]);

  const handleChange = useCallback(
    (next: EntitySelection[]) => onValueChange(next.map((selection) => selection.id)),
    [onValueChange],
  );

  return (
    <EntitySelector config={config} onValueChange={handleChange} value={selections}>
      <EntitySelectorTrigger chips fill placeholder="Add Project types..." />
      <EntitySelectorContent fit>
        <EntitySelectorInput />
        <IssueTypePool value={pool.id} />
        <EntitySelectorEmpty>No matching Project types</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}
